import * as THREE from 'three';
import { Font } from 'three/examples/jsm/loaders/FontLoader.js';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_PRESETS, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { SceneClock } from './lib/scene-clock.js';

// Contrat du rig partagé : interdit. Le texte est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'typeclash',
  family: 'typographic',
  danceability: 0.90,
  requiresTier: 0,
  energyLevel: 0.88,
  bpmMin: 95,
  bpmMax: 175,
  movement: 0.90,
  tonality: 0.40,
  enabled: true
};

// TYPECLASH : la typographie cinétique des écrans de festival. Les mots du
// titre et de l'artiste CLAQUENT sur les temps, s'empilent, s'étirent, se
// répètent en écho, tournent en 3D, passent du plein au contour. Le texte EST
// le visuel. LOOK VDJ party (lib/vdjLook.js). House, EDM, hip-hop, pop,
// techno.
//
// LE MATÉRIAU : GÉOMÉTRIE VECTORIELLE. Trois polices libres (OFL) converties
// au format typeface.json par tools/build-typeclash-fonts.mjs (Archivo Black,
// grotesque très grasse ; Anton, condensée ; Abril Fatface, empattements),
// lues par Font de three.js : les contours des glyphes (commandes m/l/q/b)
// sont triangulés en ShapeGeometry UNE fois par glyphe et par police, en
// cache. Chaque figure assemble ces triangles (le PLEIN) et des RUBANS le
// long des mêmes contours (le CONTOUR, élargi dans l'espace écran à 2,5 px
// constants, R1) en deux géométries ré-écrites au changement de figure
// (une fois par mesure, jamais par image). Tout mouvement et toute
// déformation (STRETCH à 300 %...) s'appliquent AUX SOMMETS dans le vertex
// shader : aucune texture de texte n'est agrandie, donc aucun escalier à
// aucune taille. Anti-crénelage : le texte est dessiné en pleine résolution
// dans une cible MSAA privée (4 échantillons, 2 au tier 0 ; la chaîne
// partagée n'en a pas), composée par-dessus le fond dans la passe finale.
// Les accents viennent des contours (Latin-1 et Latin étendu A), les titres
// longs se coupent en lignes et se réduisent à l'échelle.
//
// LES FIGURES (une par mesure, jamais la même deux fois de suite) : SLAM (un
// mot géant 130 % -> 100 % avec onde de choc), STACK (6 à 12 lignes du même
// mot, plein / contour alternés, défilant en sens opposés), STRETCH (300 %
// de hauteur, comprimé sur chaque kick), TUNNEL (le mot répété en
// profondeur, la copie la plus proche passe derrière la caméra), SPLIT (les
// lettres tombent une par une sur les doubles-croches), ECHO (traînées :
// FeedbackBuffer en demi-résolution qui relit la cible de texte, demi-vie un
// huitième de mesure ; le mot NET est toujours dessiné par-dessus en pleine
// résolution, R15), ORBIT (les lettres sur un anneau 3D incliné qui tourne).
// Plus TITLE : le titre complet en lignes, lisible pendant les deux
// premières secondes de chaque scène de 20 s (R20).
//
// RYTHME : temps = mot suivant ou plein <-> contour, séparation RGB
// (VdjPost) ; mesure = figure ; phrase = police, palette et fond (aplat,
// noir, ou la pochette très agrandie et floutée : biais de mipmap, R18) ;
// montée = les mots se succèdent sur chaque croche, la taille baisse, la
// vitesse monte, inversion fond / texte sur les temps 2 et 4 ; drop = SLAM du
// mot le plus long du titre, flash, puis STACK plein écran.
//
// Tier 0 : une seule police, sans ECHO, MSAA x2. Repli sans BPM : horloge
// libre à 120 ; sans titre : la banque de mots ; sans pochette : fonds aplat
// et noir ; sans WebGL2 : cible sans MSAA.
// Debug : ?typefig=slam|stack|stretch|tunnel|split|echo|orbit|title,
// ?typefont=<n>, ?typetitle=<texte>, ?typeartist=<texte>, ?typeprobe=1.
//
// precision highp (exception documentée) : les positions écran des rubans
// en pixels ne tiennent pas en fp16 sur la TV.

const FONT_FILES = ['fonts/typeclash-grotesk.typeface.json', 'fonts/typeclash-condensed.typeface.json', 'fonts/typeclash-serif.typeface.json'];
const CURVE_SEGMENTS = 10;             // per Bézier of a glyph: the outline's own smoothness, in triangles
const MAX_FILL_VERTS = 900000;         // the fill's vertex budget (STACK with 12 lines of a long word)
const MAX_RIBBON_SEGS = 120000;
const FIGURES = ['slam', 'stack', 'stretch', 'tunnel', 'split', 'echo', 'orbit', 'title'];
const F = { slam: 0, stack: 1, stretch: 2, tunnel: 3, split: 4, echo: 5, orbit: 6, title: 7 };
const BANK = ['PLAY', 'LOUD', 'BASS', 'DROP', 'NIGHT', 'MOVE', 'DANCE', 'NOW', 'FEEL', 'GO', 'BEAT', 'RISE'];
const TIER = {
  0: { fonts: 1, echo: false, samples: 2 },
  1: { fonts: 3, echo: true, samples: 4 },
  2: { fonts: 3, echo: true, samples: 4 }
};
const CYCLE = 20;
const TITLE_S = 2.2;
const NO_BPM = 120;
const SAFE_FLOOR = 0.5;
const OUTLINE_PX = 2.5;
const TOP_FREE = 0.80;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function mulberry(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- the words ---------------------------------------------------------------------

function cleanWords(raw) {
  return String(raw || '').normalize('NFC').toUpperCase().replace(/[‘’]/g, "'").split(/[\s\-–—_/,.()[\]!?:;"'«»+*&]+/).map((w) => w.trim()).filter((w) => w.length >= 2 && !/^\d+$/.test(w) && !/^(FEAT|FT|REMIX|EDIT|MIX|VERSION|RADIO|THE|AND|DE|LA|LE|LES|DU|DES|OF|A)$/.test(w));
}
export function wordsFrom(title, artist) {
  const t = cleanWords(title), a = cleanWords(artist);
  const seen = new Set(), out = [];
  for (const w of [...t, ...a]) if (!seen.has(w)) { seen.add(w); out.push(w); }
  let k = 0;
  while (out.length < 4 && k < BANK.length) { if (!seen.has(BANK[k])) { seen.add(BANK[k]); out.push(BANK[k]); } k++; }
  return { words: out, title: t, artist: a };
}

// --- the fonts and the glyph cache ----------------------------------------------------

let _fontsPromise = null;
function loadFonts() {
  if (_fontsPromise) return _fontsPromise;
  _fontsPromise = Promise.all(FONT_FILES.map((f) => fetch(f).then((r) => { if (!r.ok) throw new Error(f + ' HTTP ' + r.status); return r.json(); }).then((j) => new Font(j)).catch((e) => { console.warn('[typeclash] font missing', f, e); return null; })))
    .then((fonts) => { const first = fonts.find((f) => f); return first ? fonts.map((f) => f || first) : null; });
  return _fontsPromise;
}
// one glyph: fill triangles (em units, y up, pen at 0) and outline polylines
const GLYPHS = new Map();
function glyphOf(fontIdx, font, ch) {
  const key = fontIdx + ch;
  let g = GLYPHS.get(key);
  if (g) return g;
  const data = font.data, gd = data.glyphs[ch] || data.glyphs['?'];
  const adv = gd ? gd.ha / data.resolution : 0.5;
  let fill = new Float32Array(0);
  const lines = [];
  if (gd && gd.o) {
    const shapes = font.generateShapes(ch, 1);
    if (shapes.length) {
      const geo = new THREE.ShapeGeometry(shapes, CURVE_SEGMENTS);
      const pos = geo.getAttribute('position'), idx = geo.index;
      const n = idx ? idx.count : pos.count;
      fill = new Float32Array(n * 2);
      for (let i = 0; i < n; i++) { const v = idx ? idx.getX(i) : i; fill[i * 2] = pos.getX(v); fill[i * 2 + 1] = pos.getY(v); }
      geo.dispose();
      for (const sh of shapes) {
        lines.push(sh.getPoints(CURVE_SEGMENTS));
        for (const h of sh.holes) lines.push(h.getPoints(CURVE_SEGMENTS));
      }
    }
  }
  g = { adv, fill, lines };
  GLYPHS.set(key, g);
  return g;
}

// --- shaders -------------------------------------------------------------------------

const SAFE_GLSL = `
  uniform vec2 uScreen;
  uniform float uAspect;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAtt() {
    vec2 s = (gl_FragCoord.xy / uScreen * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;
// the figure: a point of the group's frame (height units) -> clip xy, fade, perspective scale
const FIGURE_GLSL = `
  precision highp float;
  uniform float uAspect, uFigure, uTime, uBeats, uPulse, uBarPulse, uSixteenth, uSpeed, uScale, uShrink, uOutline;
  uniform vec2 uScreen;
  // per vertex: aInfo = letter index, group index, letters in the group, random ; aExtra = group width, group count, parity, centre y of the glyph line
  vec4 project(vec3 p) {
    float s = 1.6 / (1.6 + p.z);
    return vec4(p.x * s / uAspect, p.y * s, 0.0, 1.0);
  }
  vec4 figure(vec2 local, vec4 info, vec4 extra) {
    float letter = info.x, group = info.y, nLetters = info.z, rnd = info.w;
    float groupW = extra.x, groupCount = extra.y, parity = extra.z;
    vec3 p = vec3(local, 0.0);
    float scale = uScale * uShrink;
    float fade = 1.0;
    if (uFigure < 0.5) {
      scale *= 1.0 + 0.30 * uPulse;
      p.xy *= scale;
    } else if (uFigure < 1.5) {
      float dir = parity > 0.5 ? 1.0 : -1.0;
      float x = p.x + dir * uTime * uSpeed * 0.6;
      x = mod(x + groupW * 0.5, groupW) - groupW * 0.5;
      p.x = x; p.xy *= scale;
    } else if (uFigure < 2.5) {
      p.x *= scale; p.y *= scale * (3.0 - 2.0 * uPulse);
    } else if (uFigure < 3.5) {
      float depth = mod(group - fract(uBeats * 0.5), groupCount) * 1.6 - 0.9;
      p.xy *= scale; p.z = depth;
      fade = (1.0 - smoothstep(groupCount * 1.6 * 0.5, groupCount * 1.6 - 0.9, depth)) * smoothstep(-0.9, -0.3, depth);
    } else if (uFigure < 4.5) {
      float t = clamp(uSixteenth - letter, 0.0, 1.0);
      float e = 1.0 - (1.0 - t) * (1.0 - t);
      p.xy *= scale;
      p.y += (1.0 - e) * 1.4 * (rnd > 0.5 ? 1.0 : -1.0);
      fade = step(0.0, uSixteenth - letter + 0.001);
    } else if (uFigure < 5.5) {
      p.xy *= scale * (1.0 + 0.08 * uPulse);
    } else if (uFigure < 6.5) {
      float ang = (letter + 0.5) / nLetters * 6.2831853 + uTime * uSpeed * 0.8;
      float R = max(0.75, groupW * 0.5 / 3.14159 + 0.35);
      float sa = sin(ang), ca = cos(ang);
      vec2 q = (local - vec2(0.0, extra.w)) * scale;
      p = vec3(sa * R + q.x, extra.w * scale + q.y - ca * 0.30, 0.5 - ca * 0.45);
      fade = 0.4 + 0.6 * (ca * 0.5 + 0.5);
    } else {
      p.xy *= scale;
    }
    vec4 c = project(p);
    return vec4(c.xy, fade, 1.6 / (1.6 + p.z));
  }
  // which of fill / outline shows for this vertex (STACK and TUNNEL alternate by parity / group)
  float wantsOutline(vec4 info, vec4 extra) {
    if (uFigure > 0.5 && uFigure < 1.5) return extra.z > 0.5 ? 1.0 - uOutline : uOutline;
    if (uFigure > 2.5 && uFigure < 3.5) return mod(info.y, 2.0) < 1.0 ? uOutline : 1.0 - uOutline;
    return uOutline;
  }
`;
const fillVertex = FIGURE_GLSL + `
  attribute vec4 aInfo, aExtra;
  varying float vFade;
  void main() {
    vec4 f = figure(position.xy, aInfo, aExtra);
    vFade = f.z;
    if (wantsOutline(aInfo, aExtra) > 0.5 || f.z <= 0.001) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    gl_Position = vec4(f.xy, 0.0, 1.0);
  }
`;
const ribbonVertex = FIGURE_GLSL + `
  attribute vec2 aA, aB;        // the segment's ends, group frame
  attribute vec4 aInfo, aExtra;
  uniform float uWidthPx;
  varying float vFade;
  void main() {
    vec4 fa = figure(aA, aInfo, aExtra), fb = figure(aB, aInfo, aExtra);
    vFade = fa.z;
    if (wantsOutline(aInfo, aExtra) < 0.5 || fa.z <= 0.001) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    // screen space: the segment, its normal, a ribbon of constant pixel width (R1), each end extended by half a width so joints close
    vec2 sa = (fa.xy * 0.5 + 0.5) * uScreen, sb = (fb.xy * 0.5 + 0.5) * uScreen;
    vec2 d = sb - sa;
    float L = length(d);
    d = L > 1e-4 ? d / L : vec2(1.0, 0.0);
    vec2 n = vec2(-d.y, d.x);
    float side = position.x, end = position.y;
    vec2 s = mix(sa, sb, end) + d * (end * 2.0 - 1.0) * uWidthPx * 0.5 + n * side * uWidthPx * 0.5;
    gl_Position = vec4(s / uScreen * 2.0 - 1.0, 0.0, 1.0);
  }
`;
const textFragment = `
  precision highp float;
  uniform vec3 uCol;
  varying float vFade;
  void main() { gl_FragColor = vec4(uCol, vFade); }
`;
const quadVertex = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const bgFragment = `
  precision highp float;
  ${SAFE_GLSL}
  uniform sampler2D uCover;
  uniform float uBgMode, uHasCover, uGain, uShock, uShockR;
  uniform vec3 uColBg, uColText;
  uniform vec2 uFit;
  varying vec2 vUv;
  void main() {
    vec3 c = vec3(0.0);
    if (uBgMode < 0.5) c = uColBg;
    else if (uBgMode > 1.5 && uHasCover > 0.5) {
      vec2 cu = (vUv - 0.5) * uFit + 0.5;
      c = texture2D(uCover, cu, 6.0).rgb * 0.45;
    }
    if (uShock > 0.001) {
      vec2 p = (vUv - 0.5) * vec2(uAspect, 1.0);
      float r = length(p);
      c += uColText * exp(-pow((r - uShockR) / 0.05, 2.0)) * uShock * 0.35;
    }
    gl_FragColor = vec4(c * safeAtt() * uGain, 1.0);
  }
`;
// a text target composed over the background (full resolution, the MSAA resolve)
const showFragment = `
  precision highp float;
  ${SAFE_GLSL}
  uniform sampler2D uTex;
  uniform float uGain, uAlpha;
  varying vec2 vUv;
  void main() { vec4 t = texture2D(uTex, vUv); gl_FragColor = vec4(t.rgb * safeAtt() * uGain, t.a * uAlpha); }
`;
const echoFragment = `
  precision highp float;
  uniform sampler2D uPrev, uText;
  uniform vec2 uShift;
  uniform float uDecay, uDither, uTime;
  varying vec2 vUv;
  float hash2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  void main() {
    vec4 prev = texture2D(uPrev, vUv - uShift) * uDecay;
    prev -= (1.0 / 255.0) * step(hash2(gl_FragCoord.xy + uTime), uDither);
    vec4 t = texture2D(uText, vUv);                       // the sharp text's half-res copy feeds the trail
    gl_FragColor = max(max(prev, 0.0), t);
  }
`;

export class TypeclashVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 2;
    this._layout = TIER[this._tier];
    const ff = urlParam('typefig');
    this._forcedFig = ff && F[ff] != null ? ff : null;
    const fn = parseInt(urlParam('typefont'), 10);
    this._forcedFont = Number.isFinite(fn) ? ((fn % 3) + 3) % 3 : null;
    this._forcedTitle = urlParam('typetitle'); this._forcedArtist = urlParam('typeartist');
    this._post = new VdjPost({ bloomStrength: 0.25, bloomThreshold: 0.78, grain: 0.015, vignette: 0.25 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    this._clock = new SceneClock({ period: CYCLE, start: 0 });
    this._rng = mulberry(0x7F4A7C15 ^ Math.floor((this._clock.elapsed + 1) * 613));
    this._decisions = [];
    if (urlParam('typeprobe') === '1' && typeof window !== 'undefined') window.__typeclash = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    this._time = 0; this._beats = 0; this._beatCount = 0; this._barCount = 0; this._phraseCount = 0;
    this._beatPeriod = 60 / NO_BPM; this._free = 0; this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrasePhase = null;
    this._lastBeatAt = -10; this._lastBarAt = -10; this._build = 0; this._lastDropKey = null; this._dropAt = -10; this._dropStage = 0;
    this._shock = 0; this._shockR = 0; this._flash = 0; this._flashNow = 0;
    this._fig = 'title'; this._lastFig = null; this._wordIdx = 0; this._outline = 0; this._fontIdx = 0; this._pal = 0; this._bgMode = 0; this._titleUntil = TITLE_S;
    this._words = { words: BANK.slice(0, 4), title: [], artist: [] }; this._wordsKey = undefined; this._wordOrder = [0, 1, 2, 3];
    this._fonts = null;
    this._coverUrl = undefined; this._cover = null; this._coverPending = null;
    this._fillCount = 0; this._ribbonCount = 0; this._coverage = 0.05; this._lumSince = 0; this._figKey = '';
    this._size = new THREE.Vector2(); this._sinceRender = 1 / 60;

    const safe = { uScreen: { value: new THREE.Vector2(1280, 720) }, uAspect: { value: 16 / 9 }, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._gain = { value: 1 };
    this._colText = new THREE.Color(1, 1, 1); this._colBg = new THREE.Color(0, 0, 0);
    // the figure's uniforms, shared by the fill and the ribbons
    this._fu = {
      uAspect: safe.uAspect, uScreen: safe.uScreen, uFigure: { value: 7 }, uTime: { value: 0 }, uBeats: { value: 0 }, uPulse: { value: 0 }, uBarPulse: { value: 0 }, uSixteenth: { value: 0 },
      uSpeed: { value: 1 }, uScale: { value: 1 }, uShrink: { value: 1 }, uOutline: { value: 0 }, uWidthPx: { value: OUTLINE_PX }
    };
    this._fillCol = { value: new THREE.Color() }; this._ribbonCol = { value: new THREE.Color() };
    // the fill: triangles with per-vertex figure attributes
    this._fillGeo = new THREE.BufferGeometry();
    this._fillArr = { position: new Float32Array(MAX_FILL_VERTS * 3), aInfo: new Float32Array(MAX_FILL_VERTS * 4), aExtra: new Float32Array(MAX_FILL_VERTS * 4) };
    this._fillGeo.setAttribute('position', new THREE.BufferAttribute(this._fillArr.position, 3));
    this._fillGeo.setAttribute('aInfo', new THREE.BufferAttribute(this._fillArr.aInfo, 4));
    this._fillGeo.setAttribute('aExtra', new THREE.BufferAttribute(this._fillArr.aExtra, 4));
    this._fillGeo.setDrawRange(0, 0); this._fillGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    // DoubleSide: the ribbon's (side, end) frame flips the winding (determinant -1), and a figure may mirror a glyph
    this._fillMat = new THREE.ShaderMaterial({ uniforms: Object.assign({ uCol: this._fillCol }, this._fu), vertexShader: fillVertex, fragmentShader: textFragment, transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide });
    this._fill = new THREE.Mesh(this._fillGeo, this._fillMat); this._fill.frustumCulled = false;
    // the ribbons: one quad (4 vertices) per contour segment, instanced
    this._ribbonGeo = new THREE.InstancedBufferGeometry();
    const quad = new THREE.BufferGeometry();
    quad.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0]), 3));
    quad.setIndex([0, 1, 2, 0, 2, 3]);
    this._ribbonGeo.index = quad.index; this._ribbonGeo.setAttribute('position', quad.getAttribute('position'));
    this._ribArr = { aA: new Float32Array(MAX_RIBBON_SEGS * 2), aB: new Float32Array(MAX_RIBBON_SEGS * 2), aInfo: new Float32Array(MAX_RIBBON_SEGS * 4), aExtra: new Float32Array(MAX_RIBBON_SEGS * 4) };
    this._ribbonGeo.setAttribute('aA', new THREE.InstancedBufferAttribute(this._ribArr.aA, 2));
    this._ribbonGeo.setAttribute('aB', new THREE.InstancedBufferAttribute(this._ribArr.aB, 2));
    this._ribbonGeo.setAttribute('aInfo', new THREE.InstancedBufferAttribute(this._ribArr.aInfo, 4));
    this._ribbonGeo.setAttribute('aExtra', new THREE.InstancedBufferAttribute(this._ribArr.aExtra, 4));
    this._ribbonGeo.instanceCount = 0; this._ribbonGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._ribbonMat = new THREE.ShaderMaterial({ uniforms: Object.assign({ uCol: this._ribbonCol }, this._fu), vertexShader: ribbonVertex, fragmentShader: textFragment, transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide });
    this._ribbons = new THREE.Mesh(this._ribbonGeo, this._ribbonMat); this._ribbons.frustumCulled = false;
    this._textScene = new THREE.Scene(); this._textScene.add(this._fill); this._textScene.add(this._ribbons);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10); this._ortho.position.z = 1;
    this._rtText = null;
    // the background, then the echo's trail, then the sharp text
    this._bu = Object.assign({}, safe, { uCover: { value: null }, uBgMode: { value: 1 }, uHasCover: { value: 0 }, uGain: this._gain, uShock: { value: 0 }, uShockR: { value: 0 }, uColBg: { value: this._colBg }, uColText: { value: this._colText }, uFit: { value: new THREE.Vector2(1, 1) } });
    this._bgMat = new THREE.ShaderMaterial({ uniforms: this._bu, vertexShader: quadVertex, fragmentShader: bgFragment, depthTest: false, depthWrite: false });
    this._bg = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._bgMat); this._bg.frustumCulled = false; this._bg.renderOrder = -1000;
    const self = this;
    this._bg.onBeforeRender = function (renderer) { self._render(renderer); };
    this._su = Object.assign({}, safe, { uTex: { value: null }, uGain: this._gain, uAlpha: { value: 1 } });
    this._showMat = new THREE.ShaderMaterial({ uniforms: this._su, vertexShader: quadVertex, fragmentShader: showFragment, transparent: true, depthTest: false, depthWrite: false });
    this._show = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._showMat); this._show.frustumCulled = false; this._show.renderOrder = -998;
    this._feedback = this._layout.echo ? new FeedbackBuffer({ scale: 0.5 }) : null;
    this._eu = { uPrev: { value: null }, uText: { value: null }, uShift: { value: new THREE.Vector2() }, uDecay: { value: 0.9 }, uDither: { value: 0.5 }, uTime: { value: 0 } };
    this._echoMat = new THREE.ShaderMaterial({ uniforms: this._eu, vertexShader: quadVertex, fragmentShader: echoFragment, depthTest: false, depthWrite: false });
    this._passScene = new THREE.Scene(); this._passQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._echoMat); this._passQuad.frustumCulled = false; this._passScene.add(this._passQuad);
    this._eshowU = Object.assign({}, safe, { uTex: { value: null }, uGain: this._gain, uAlpha: { value: 0.8 } });
    this._echoShowMat = new THREE.ShaderMaterial({ uniforms: this._eshowU, vertexShader: quadVertex, fragmentShader: showFragment, transparent: true, depthTest: false, depthWrite: false });
    this._echoShow = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._echoShowMat); this._echoShow.frustumCulled = false; this._echoShow.renderOrder = -999; this._echoShow.visible = false;
    this._group = new THREE.Group(); this._group.add(this._bg); this._group.add(this._echoShow); this._group.add(this._show); scene.add(this._group);
    loadFonts().then((fonts) => { if (!this._scene) return; this._fonts = fonts; this._figKey = ''; });
  }

  _say(what) { this._decisions.push({ t: +this._time.toFixed(2), bar: this._barCount, what }); if (this._decisions.length > 64) this._decisions.shift(); }

  _setWords(profile) {
    const title = this._forcedTitle != null ? this._forcedTitle : (profile && profile.title);
    const artist = this._forcedArtist != null ? this._forcedArtist : (profile && profile.artist);
    const key = (title || '') + '|' + (artist || '');
    if (key === this._wordsKey) return;
    this._wordsKey = key;
    this._words = wordsFrom(title, artist);
    this._wordOrder = this._words.words.map((_, i) => i);
    this._figKey = '';
  }

  _loadCover(url) {
    this._coverUrl = url;
    if (!url) { this._dropCover(); return; }
    const token = {};
    this._coverPending = token;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (texture) => {
      if (this._coverPending !== token) { texture.dispose(); return; }
      this._coverPending = null;
      if (this._cover) this._cover.dispose();
      this._cover = texture; this._bu.uCover.value = texture; this._bu.uHasCover.value = 1;
    }, () => { if (this._coverPending === token) { this._coverPending = null; this._dropCover(); } });
  }
  _dropCover() { if (this._cover) this._cover.dispose(); this._cover = null; this._bu.uCover.value = null; this._bu.uHasCover.value = 0; }

  // --- layout -------------------------------------------------------------------------

  _font() { return this._fonts ? this._fonts[Math.min(this._fontIdx, this._layout.fonts - 1)] : null; }
  _capEm(font) { return (font.data.capHeight || font.data.resolution * 0.7) / font.data.resolution; }
  _wordWidth(word, font, capH, tracking) {
    const fi = this._fontIdx, em = capH / this._capEm(font);
    let w = 0;
    for (let k = 0; k < word.length; k++) w += (word[k] === ' ' ? 0.3 : glyphOf(fi, font, word[k]).adv) * em + (k ? tracking * capH : 0);
    return w;
  }
  _fitCap(word, font, capH0, maxW, tracking) {
    const w = this._wordWidth(word, font, capH0, tracking);
    return w > maxW ? capH0 * maxW / w : capH0;
  }
  // a word's glyphs (fill triangles and outline segments), centred on (gx, gy), appended to the figure's buffers
  _placeWord(word, font, capH, tracking, gx, gy, groupIdx, extra) {
    const fi = this._fontIdx, em = capH / this._capEm(font);
    const width = this._wordWidth(word, font, capH, tracking);
    let pen = gx - width / 2;
    const baseline = gy - capH / 2;
    const FA = this._fillArr, RA = this._ribArr;
    const ex = [extra[0] || width, extra[1] || 1, extra[2] || 0, gy];
    for (let k = 0; k < word.length; k++) {
      const ch = word[k];
      if (ch === ' ') { pen += 0.3 * em; continue; }
      const g = glyphOf(fi, font, ch);
      const info = [k, groupIdx, word.length, this._rng()];
      const nv = g.fill.length / 2;
      if (this._fillCount + nv <= MAX_FILL_VERTS) {
        let o = this._fillCount;
        for (let i = 0; i < nv; i++, o++) {
          FA.position[o * 3] = pen + g.fill[i * 2] * em; FA.position[o * 3 + 1] = baseline + g.fill[i * 2 + 1] * em; FA.position[o * 3 + 2] = 0;
          FA.aInfo.set(info, o * 4); FA.aExtra.set(ex, o * 4);
        }
        this._fillCount = o;
      }
      for (const line of g.lines) {
        const n = line.length;
        for (let i = 0; i < n; i++) {
          if (this._ribbonCount >= MAX_RIBBON_SEGS) break;
          const a = line[i], b = line[(i + 1) % n];
          const s = this._ribbonCount;
          RA.aA[s * 2] = pen + a.x * em; RA.aA[s * 2 + 1] = baseline + a.y * em;
          RA.aB[s * 2] = pen + b.x * em; RA.aB[s * 2 + 1] = baseline + b.y * em;
          RA.aInfo.set(info, s * 4); RA.aExtra.set(ex, s * 4);
          this._ribbonCount++;
        }
      }
      pen += g.adv * em + tracking * capH;
    }
    return width;
  }

  _buildFigure() {
    const font = this._font();
    this._fillCount = 0; this._ribbonCount = 0;
    if (!font) { this._fillGeo.setDrawRange(0, 0); this._ribbonGeo.instanceCount = 0; return; }
    const aspect = this._bu.uAspect.value, W = 2 * aspect, maxW = W * 0.86;
    const words = this._words.words, word = words[this._wordOrder[this._wordIdx % this._wordOrder.length]] || 'PLAY';
    const fig = this._fig;
    let coverage = 0;
    if (fig === 'title') {
      const title = this._words.title.length ? this._words.title : words.slice(0, 2);
      const lines = [];
      let cap = 0.26, line = [];
      const widthOf = (ws) => this._wordWidth(ws.join(' '), font, cap, 0);
      for (const w of title) { const trial = [...line, w]; if (line.length && widthOf(trial) > maxW) { lines.push(line); line = [w]; } else line = trial; }
      if (line.length) lines.push(line);
      const hasArtist = this._words.artist.length > 0;
      const total = lines.length * cap * 1.35 + (hasArtist ? cap * 0.45 * 2.2 : 0);
      if (total > 2 * TOP_FREE) cap *= 2 * TOP_FREE / total;
      const lh = cap * 1.35, aCap = cap * 0.45;
      const top = (lines.length * lh + (hasArtist ? aCap * 2.2 : 0)) / 2;
      lines.forEach((ws, li) => {
        const text = ws.join(' ');
        const c = this._fitCap(text, font, cap, maxW, 0);
        const w = this._placeWord(text, font, c, 0, 0, top - lh * (li + 0.5), li, [0, lines.length, 0]);
        coverage += c * w * 0.55;
      });
      if (hasArtist) { const text = this._words.artist.join(' '); const c = this._fitCap(text, font, aCap, maxW, 0.05); this._placeWord(text, font, c, 0.05, 0, top - lines.length * lh - aCap * 1.2, lines.length, [0, lines.length + 1, 1]); }
    } else if (fig === 'stack') {
      const n = 6 + Math.floor(this._rng() * 7), lineH = Math.min(0.26, 2 * TOP_FREE / n), cap = lineH * 0.78;
      const w1 = this._wordWidth(word, font, cap, 0.02) + cap * 0.6, copies = Math.max(3, Math.ceil(W * 1.3 / w1) + 1), rowLen = copies * w1;
      for (let li = 0; li < n; li++) {
        const y = (n - 1) / 2 * lineH - li * lineH;
        for (let c = 0; c < copies; c++) this._placeWord(word, font, cap, 0.02, (c - (copies - 1) / 2) * w1, y, li, [rowLen, n, li % 2]);
      }
      coverage = n * cap * Math.min(W, rowLen) * 0.55;
    } else if (fig === 'tunnel') {
      const n = 6, cap = this._fitCap(word, font, 0.5, maxW * 0.9, 0.02);
      for (let g = 0; g < n; g++) this._placeWord(word, font, cap, 0.02, 0, 0, g, [0, n, 0]);
      coverage = cap * this._wordWidth(word, font, cap, 0.02) * 1.2;
    } else if (fig === 'orbit') {
      const cap = Math.min(0.42, 2.2 / Math.max(3, word.length));
      const w = this._placeWord(word, font, cap, 0.3, 0, 0, 0, [0, 1, 0]);
      coverage = cap * w * 0.5;
    } else if (fig === 'split') {
      const cap = this._fitCap(word, font, 0.55, maxW, 0.18);
      const w = this._placeWord(word, font, cap, 0.18, 0, 0, 0, [0, 1, 0]);
      coverage = cap * w * 0.5;
    } else {
      const cap0 = fig === 'stretch' ? 0.42 : 0.6;
      const cap = this._fitCap(word, font, cap0, maxW / (fig === 'slam' ? 1.3 : 1.0), 0.0);
      const w = this._placeWord(word, font, cap, 0.0, 0, 0, 0, [0, 1, 0]);
      coverage = cap * w * (fig === 'stretch' ? 1.5 : 0.6);
    }
    for (const name of ['position', 'aInfo', 'aExtra']) this._fillGeo.getAttribute(name).needsUpdate = true;
    this._fillGeo.setDrawRange(0, this._fillCount);
    for (const name of ['aA', 'aB', 'aInfo', 'aExtra']) this._ribbonGeo.getAttribute(name).needsUpdate = true;
    this._ribbonGeo.instanceCount = this._ribbonCount;
    this._fu.uFigure.value = F[fig];
    this._coverage = clamp01(coverage / (W * 2));
    this._figKey = fig + '|' + word + '|' + this._fontIdx + '|' + aspect.toFixed(3) + '|' + this._wordsKey;
  }

  // --- the VJ -------------------------------------------------------------------------

  _pickFigure(exclude) {
    if (this._forcedFig) return this._forcedFig;
    const pool = FIGURES.filter((f) => f !== 'title' && f !== exclude && (f !== 'echo' || this._layout.echo));
    return pool[Math.floor(this._rng() * pool.length)];
  }
  _nextWord() {
    const n = this._wordOrder.length;
    this._wordIdx++;
    if (this._wordIdx % n === 0) { for (let i = n - 1; i > 0; i--) { const j = Math.floor(this._rng() * (i + 1)); [this._wordOrder[i], this._wordOrder[j]] = [this._wordOrder[j], this._wordOrder[i]]; } }
  }
  _applyPalette(profile) {
    const pal = profile && profile.artworkPalette;
    const src = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff2bd6, 0x2bffea, 0xffc21a];
    const key = src.join('|') + '#' + this._pal + '#' + this._bgMode;
    if (key === this._palKey) return;
    this._palKey = key;
    const cols = src.map((h) => vdjColor(h));
    const text = cols[this._pal % cols.length], bg = cols[(this._pal + 1) % cols.length].clone();
    this._colText.copy(text);
    const hb = {}, ht = {}; bg.getHSL(hb); text.getHSL(ht);
    if (Math.min(Math.abs(hb.h - ht.h), 1 - Math.abs(hb.h - ht.h)) < 0.25) bg.offsetHSL(0.4, 0, 0);
    bg.multiplyScalar(0.22);
    this._colBg.copy(bg);
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== undefined && profile.playbackState !== 'PLAYING';
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    this._clock.update(dt);
    this._setWords(profile);
    if ((profile.artworkUrl || null) !== this._coverUrl) this._loadCover(profile.artworkUrl || null);
    this._applyPalette(profile);
    if (paused) return;
    this._time += dt; this._sinceRender += dt;
    const director = profile.director || {};
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    this._beatPeriod = 60 / THREE.MathUtils.clamp(bpm, 60, 200);

    const beatPhase = director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false;
    if (beatPhase != null) { if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true; this._lastBeatPhase = beatPhase; }
    else { this._free += dt; if (this._free >= this._beatPeriod) { this._free -= this._beatPeriod; beatEdge = true; } }
    this._beats += dt / this._beatPeriod;
    const bp = beatPhase != null ? beatPhase : this._free / this._beatPeriod;
    const barPhase = director.bar != null ? director.bar : profile.barPhase;
    let barEdge = false;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) barEdge = true; this._lastBarPhase = barPhase; }
    else if (beatEdge && this._beatCount % 4 === 0 && this._beatCount > 0) barEdge = true;
    const phrasePhase = director.phrase != null ? director.phrase : profile.phrasePhase;
    let phraseEdge = false;
    if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) phraseEdge = true; this._lastPhrasePhase = phrasePhase; }
    else if (beatEdge && this._beatCount % 32 === 0 && this._beatCount > 0) phraseEdge = true;
    if (beatEdge) { this._beatCount++; this._lastBeatAt = this._time; this._beats = Math.round(this._beats); }
    if (barEdge) { this._barCount++; this._lastBarAt = this._time; }
    if (phraseEdge) this._phraseCount++;

    const ev = director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    const build = beatsToDrop != null && beatsToDrop < 32 ? clamp01((32 - beatsToDrop) / 28) : (director.mode === 'build' ? 0.4 : 0);
    this._build += (build - this._build) * Math.min(1, dt / 0.4);
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    let drop = false;
    if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      if (key !== this._lastDropKey) { this._lastDropKey = key; drop = true; }
    }

    const sceneStart = this._clock.wrapped || this._time <= dt;
    if (sceneStart) { this._fig = 'title'; this._lastFig = 'title'; this._titleUntil = this._time + TITLE_S; this._say('TITLE (scene)'); }
    const titleHold = this._time < this._titleUntil;

    if (drop && this._time - this._dropAt > 4 * this._beatPeriod && !titleHold) {
      this._dropAt = this._time; this._build = 0; this._dropStage = 1;
      const title = this._words.title.length ? this._words.title : this._words.words;
      let longest = 0; for (let i = 0; i < this._words.words.length; i++) if (title.includes(this._words.words[i]) && this._words.words[i].length > this._words.words[longest].length) longest = i;
      this._wordOrder = this._words.words.map((_, i) => i); this._wordIdx = longest;
      this._fig = 'slam'; this._lastFig = 'slam'; this._outline = 0;
      this._shock = 1; this._shockR = 0;
      if (this._comfort.requestFlash(this._time)) this._flash = VDJ_DROP_FLASH;
      this._say('DROP slam ' + this._words.words[longest]);
    } else if (barEdge && !titleHold) {
      if (this._dropStage === 1) { this._dropStage = 0; this._fig = 'stack'; this._say('DROP stack'); }
      else { const f = this._pickFigure(this._lastFig); this._fig = f; this._say('fig -> ' + f); }
      this._lastFig = this._fig;
      if (this._fig === 'slam') { this._shock = 1; this._shockR = 0; }
    } else if (barEdge && titleHold && this._time + 0.5 >= this._titleUntil) {
      this._titleUntil = this._time;
      const f = this._pickFigure('title'); this._fig = f; this._lastFig = f; this._say('fig -> ' + f + ' (after title)');
    }
    if (phraseEdge) {
      if (this._layout.fonts > 1 && this._forcedFont == null) this._fontIdx = (this._fontIdx + 1 + Math.floor(this._rng() * 2)) % 3;
      this._pal = (this._pal + 1) % 3;
      this._bgMode = (this._bgMode + 1) % 3;
      this._say('phrase: font ' + this._fontIdx + ' bg ' + this._bgMode);
    }
    if (this._forcedFont != null) this._fontIdx = this._forcedFont;
    const eighth = this._build > 0.3 && this._fig !== 'title';
    const eighthEdge = eighth && Math.floor(this._beats * 2 + 1e-4) !== Math.floor((this._beats - dt / this._beatPeriod) * 2 + 1e-4);
    if ((beatEdge || eighthEdge) && !titleHold && this._fig !== 'split' && this._dropStage !== 1) {
      if (this._fig !== 'stack' || beatEdge) this._nextWord();
      if (this._rng() < 0.5) this._outline = 1 - this._outline;
      if (this._fig === 'slam') { this._shock = 1; this._shockR = 0; }
    }
    if (beatEdge && this._fig === 'split' && this._beatCount % 4 === 1) this._nextWord();

    const word = this._words.words[this._wordOrder[this._wordIdx % this._wordOrder.length]];
    const key = this._fig + '|' + word + '|' + this._fontIdx + '|' + this._bu.uAspect.value.toFixed(3) + '|' + this._wordsKey;
    if (key !== this._figKey) this._buildFigure();

    const U = this._fu, since = this._time - this._lastBeatAt;
    U.uTime.value = this._time; U.uBeats.value = this._beats;
    U.uPulse.value = Math.exp(-since / (this._beatPeriod * 0.28));
    U.uBarPulse.value = Math.exp(-(this._time - this._lastBarAt) / (this._beatPeriod * 0.6));
    U.uSixteenth.value = (this._beats - Math.floor(this._beats / 4) * 4) * 4;
    U.uSpeed.value = 1 + 1.5 * this._build;
    U.uShrink.value = 1 - 0.45 * this._build;
    U.uOutline.value = this._outline;
    const inBar = (this._beatCount - 1) % 4;
    const invert = this._build > 0.3 && (inBar === 1 || inBar === 3) && since < this._beatPeriod * 0.5;
    const textCol = invert ? this._colBg : this._colText;
    if (invert) { this._bu.uBgMode.value = 0; this._bu.uColBg.value.copy(this._colText).multiplyScalar(0.8); }
    else { this._bu.uBgMode.value = this._bgMode === 2 && !this._cover ? 1 : this._bgMode; this._bu.uColBg.value.copy(this._colBg); }
    // the fill is the palette just under the bloom threshold, the outline's core brighter (R1)
    this._fillCol.value.copy(textCol).multiplyScalar(invert ? 3.0 : 0.72);
    this._ribbonCol.value.copy(textCol).lerp(new THREE.Color(1, 1, 1), 0.75);
    this._shock = Math.max(0, this._shock - dt / (this._beatPeriod * 0.9)); this._shockR += dt * 2.2;
    this._bu.uShock.value = this._shock; this._bu.uShockR.value = this._shockR;
    this._post.update(vdjBeatChroma(bp, this._beatPeriod));
    this._flashNow = this._flash; this._flash = 0;
    const bgLum = this._bu.uBgMode.value < 0.5 ? 0.3 : this._bu.uBgMode.value > 1.5 ? 0.25 : 0.0;
    const est = bgLum * (1 - this._coverage) + (this._outline ? 0.35 : 0.8) * this._coverage + this._flashNow * 0.6;
    this._lumSince += dt;
    if (this._lumSince > 0.25) { this._gain.value = this._comfort.measure(est, this._lumSince); this._lumSince = 0; }
    this._echoOn = this._fig === 'echo' && !!this._feedback;
    this._echoShow.visible = this._echoOn;
  }

  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    const prev = renderer.getRenderTarget();
    let W = this._size.x, H = this._size.y;
    if (prev) { W = prev.width; H = prev.height; }
    const aspect = W / Math.max(1, H);
    this._fu.uScreen.value.set(W, H);
    if (Math.abs(aspect - this._fu.uAspect.value) > 1e-3) { this._fu.uAspect.value = aspect; this._figKey = ''; }
    if (this._figKey === '') this._buildFigure();
    if (this._cover && this._cover.image) {
      const img = this._cover.image, ta = (img.width || 1) / (img.height || 1), ratio = aspect / ta;
      this._bu.uFit.value.set(ratio > 1 ? 1 : ratio, ratio > 1 ? 1 / ratio : 1).multiplyScalar(0.6);
    }
    // the text target: full resolution, MSAA when the context allows it
    if (!this._rtText || this._rtText.width !== W || this._rtText.height !== H) {
      if (this._rtText) this._rtText.dispose();
      const samples = renderer.capabilities.isWebGL2 ? this._layout.samples : 0;
      this._rtText = new THREE.WebGLRenderTarget(W, H, { type: THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false, samples });
      this._su.uTex.value = this._rtText.texture;
    }
    const prevClear = new THREE.Color(); renderer.getClearColor(prevClear); const prevAlpha = renderer.getClearAlpha();
    renderer.setRenderTarget(this._rtText);
    renderer.setClearColor(0x000000, 0);
    renderer.clear(true, false, false);
    renderer.render(this._textScene, this._ortho);
    renderer.setClearColor(prevClear, prevAlpha);
    // the echo: the trail at half resolution reads the sharp text's target; the sharp text is drawn over it
    if (this._echoOn) {
      const fb = this._feedback;
      fb.ensureSize(renderer);
      this._eu.uDecay.value = Math.pow(0.5, Math.min(this._sinceRender, 1) / (this._beatPeriod * 4 / 8));
      this._eu.uShift.value.set(0.012 * Math.cos(this._time * 0.7), 0.012 * Math.sin(this._time * 0.9));
      this._eu.uTime.value = this._time; this._eu.uPrev.value = fb.read.texture; this._eu.uText.value = this._rtText.texture;
      renderer.setRenderTarget(fb.write);
      renderer.render(this._passScene, this._ortho);
      this._eshowU.uTex.value = fb.write.texture;
      fb.swap();
    } else if (this._feedback && this._feedback.width && this._echoWas) this._feedback.clear(renderer);
    this._echoWas = this._echoOn;
    this._sinceRender = 0;
    renderer.setRenderTarget(prev);
    if (this._flashNow > 0) { this._bu.uColBg.value.lerp(new THREE.Color(1, 1, 1), this._flashNow); this._bu.uBgMode.value = 0; }
  }

  onTrackChange(profile) { this._setWords(profile); }

  get probe() {
    return {
      tier: this._tier, fig: this._fig, word: this._words.words[this._wordOrder[this._wordIdx % this._wordOrder.length]], words: this._words.words, title: this._words.title, artist: this._words.artist,
      font: this._fontIdx, fontsLoaded: !!this._fonts, fillVerts: this._fillCount, ribbons: this._ribbonCount, outline: this._outline, bg: this._bu.uBgMode.value,
      build: +this._build.toFixed(2), bars: this._barCount, beats: +this._beats.toFixed(2), scene: +this._clock.t.toFixed(2), titleHold: this._time < this._titleUntil, decisions: this._decisions.slice(-10),
      coverage: +this._coverage.toFixed(3), gain: +this._gain.value.toFixed(2), echo: !!this._echoOn, samples: this._rtText ? this._rtText.samples : null
    };
  }

  dispose() {
    if (this._bg) this._bg.onBeforeRender = null;
    if (this._group && this._scene) this._scene.remove(this._group);
    for (const m of [this._fillMat, this._ribbonMat, this._bgMat, this._showMat, this._echoMat, this._echoShowMat]) if (m) m.dispose();
    for (const g of [this._fillGeo, this._ribbonGeo]) if (g) g.dispose();
    for (const m of [this._bg, this._show, this._echoShow, this._passQuad]) if (m) m.geometry.dispose();
    if (this._rtText) this._rtText.dispose();
    if (this._feedback) this._feedback.dispose();
    this._dropCover();
    this._scene = null;
  }
}
